// Full-screen blocking overlay implementing the Two-Step Acceptance Gate.
//
// Architectural contract (§27/§40):
//   * Mounted by MemberLayout on top of protected member content.
//   * The protected route tree is NEVER mounted while the gate is active or
//     while the acceptance check is still resolving — the layout ensures
//     ``<Outlet />`` renders only after ``required_next === null``.
//   * The URL is preserved. Deep-link/refresh keeps the browser location
//     unchanged; the layout re-mounts the gate over whichever protected
//     route the user requested and, after both steps are accepted, the
//     Outlet reveals that route naturally.
//   * The only visible escape is Log out (§13).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/modules/growing-circle/lib/api";
import { useAcceptance } from "@/modules/growing-circle/context/AcceptanceContext";
import { useAuth } from "@/modules/growing-circle/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import StepRenderer from "./StepRenderer";

const LEGAL_LINKS = [
  { label: "Terms & Conditions", href: "https://www.iubenda.com/terms-and-conditions/84007906" },
  { label: "Privacy Policy", href: "https://www.iubenda.com/privacy-policy/84007906" },
  { label: "Cookie Policy", href: "https://www.iubenda.com/privacy-policy/84007906/cookie-policy" },
];

function useEscapeGuard() {
  // §13/§32: Escape must never dismiss the gate.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") e.preventDefault(); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
}

function useFocusTrap(rootRef, active) {
  // §32: keyboard focus trapped inside the modal container.
  useEffect(() => {
    if (!active) return;
    const root = rootRef.current;
    if (!root) return;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const first = () => Array.from(root.querySelectorAll(selector))[0];
    const last = () => {
      const all = Array.from(root.querySelectorAll(selector));
      return all[all.length - 1];
    };
    const handler = (e) => {
      if (e.key !== "Tab") return;
      const f = first();
      const l = last();
      if (!f || !l) return;
      if (e.shiftKey && document.activeElement === f) {
        e.preventDefault();
        l.focus();
      } else if (!e.shiftKey && document.activeElement === l) {
        e.preventDefault();
        f.focus();
      }
    };
    root.addEventListener("keydown", handler);
    setTimeout(() => first()?.focus(), 0);
    return () => root.removeEventListener("keydown", handler);
  }, [rootRef, active]);
}

function StepShell({ testId, terms, onAccept, onLogout, submitting }) {
  const [checked, setChecked] = useState(false);
  useEffect(() => { setChecked(false); }, [terms?.version, terms?.type]);
  const rootRef = useRef(null);
  useFocusTrap(rootRef, true);
  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-start sm:items-center justify-center overflow-y-auto"
    >
      <div className="max-w-3xl w-full my-6 sm:my-10 mx-4 sm:mx-6 bg-card border border-border rounded-3xl p-6 sm:p-10 shadow-2xl">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
          {terms.type === "protected_content_terms" ? "Step 1 of 2" : "Step 2 of 2"}
        </p>
        <h1
          id={`${testId}-title`}
          className="font-heading text-2xl sm:text-3xl leading-tight tracking-tight mb-6"
        >
          {terms.title}
        </h1>
        <div className="max-h-[52vh] overflow-y-auto pr-2 -mr-2">
          <StepRenderer body={terms.body} />
        </div>
        <label className="mt-8 flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-1 accent-primary w-4 h-4"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid={`${testId.replace("-modal", "")}-checkbox`}
            aria-describedby={`${testId}-checkbox-label`}
          />
          <span id={`${testId}-checkbox-label`} className="text-sm sm:text-[15px] text-foreground/90 leading-relaxed">
            {terms.checkbox_text}
          </span>
        </label>
        <div className="mt-8 flex flex-col sm:flex-row-reverse gap-3">
          <Button
            className="btn-gold rounded-full h-12 px-6 text-xs sm:text-sm font-medium uppercase tracking-[0.14em]"
            disabled={!checked || submitting}
            onClick={() => onAccept(terms)}
            data-testid={`${testId.replace("-modal", "")}-accept-btn`}
          >
            {submitting ? "One moment…" : terms.button_text}
          </Button>
          <Button
            variant="ghost"
            className="rounded-full h-12 px-6 text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
            onClick={onLogout}
            data-testid="acceptance-logout-btn"
          >
            Log out
          </Button>
        </div>
        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground">
          {LEGAL_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function FailureShell({ message, onRetry, onLogout }) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-center justify-center"
      data-testid="acceptance-failure"
    >
      <div className="max-w-md w-full mx-4 bg-card border border-border rounded-3xl p-8 text-center">
        <h2 className="font-heading text-2xl tracking-tight mb-3">We could not verify your access conditions.</h2>
        <p className="text-muted-foreground text-sm mb-8">Please try again.</p>
        <div className="flex flex-col sm:flex-row-reverse gap-3 justify-center">
          <Button
            className="btn-gold rounded-full h-11 px-6 text-xs sm:text-sm font-medium uppercase tracking-[0.14em]"
            onClick={onRetry}
            data-testid="acceptance-retry-btn"
          >
            Retry
          </Button>
          <Button
            variant="ghost"
            className="rounded-full h-11 px-6 text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
            onClick={onLogout}
            data-testid="acceptance-logout-btn"
          >
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AcceptanceGate() {
  const { status, refresh, failure } = useAcceptance();
  const { logout } = useAuth();
  const navigate = useNavigate();
  useEscapeGuard();

  const required = status?.required_next;
  const [terms, setTerms] = useState(null);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadTerms = useCallback(async (type) => {
    if (!type) return;
    setLoadingTerms(true);
    setLoadError(false);
    try {
      const { data } = await api.get("/legal/terms/current", { params: { type, language: "en" } });
      setTerms(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoadingTerms(false);
    }
  }, []);

  useEffect(() => {
    if (required) loadTerms(required);
    else setTerms(null);
  }, [required, loadTerms]);

  const onAccept = async (t) => {
    setSubmitting(true);
    try {
      await api.post("/me/accept", { type: t.type, version: t.version, language: t.language });
      await refresh();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "terms_version_changed") {
        toast.error("The conditions were updated. Please review the new text.");
        await loadTerms(t.type);
      } else {
        toast.error(formatApiError(e?.response?.data?.detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/growing-circle/login", { replace: true });
  };

  if (failure || loadError) {
    return (
      <FailureShell
        message={failure?.message}
        onRetry={async () => { await refresh(); if (required) loadTerms(required); }}
        onLogout={onLogout}
      />
    );
  }

  if (loadingTerms || !terms) {
    return (
      <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-center justify-center">
        <p className="font-heading text-lg tracking-[0.18em] uppercase text-muted-foreground animate-pulse">
          Checking access…
        </p>
      </div>
    );
  }

  const testId = terms.type === "protected_content_terms" ? "step1-modal" : "step2-modal";
  return (
    <StepShell
      testId={testId}
      terms={terms}
      onAccept={onAccept}
      onLogout={onLogout}
      submitting={submitting}
    />
  );
}
